import React, { FC, useCallback, useState } from 'react';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { FormProvider, useForm, useFormState } from 'react-hook-form';
import { object, string } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import AttachmentDropzoneField from '@buyside/components/presentation/IdeaForm/WriteupAndAttachments/Attachments/AttachmentForm/attachmentDropzoneField';
import Button, { BUTTON_SIZES, BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import Modal from '@buyside/components/presentation/Modal/modal';
import SelectField from '@buyside/components/presentation/Fields/selectField';

import { labelize } from '@buyside/util/arrayHelper';
import { IdeaFormInput } from '@buyside/util/researchSubmission';

import styles from './attachmentFormStyles.scss';

const { ideaAttachmentTypes } = require('buyside/util/constantsFromRails.json');

const AttachmentForm: FC<{
  attachment?: IdeaFormInput['attachments'][number];
  onSubmitCallback: (values: IdeaFormInput['attachments'][number]) => void;
  researchType: string;
}> = ({ attachment, onSubmitCallback, researchType }) => {
  const { isSubmitting } = useFormState();

  const isFormattedThesis = researchType === 'Formatted Thesis';
  const isThesis = researchType === 'Thesis';

  const [isOpen, setIsOpen] = useState(false);

  const methods = useForm<IdeaFormInput['attachments'][number]>({
    values: attachment || {
      id: null,
      attachmentType: null,
      file: null,
      filename: '',
      fromUpdate: false,
      researchType,
      url: ''
    },
    resolver: yupResolver(
      object({
        attachmentType: string()
          .nullable()
          .when('researchType', {
            is: (researchType: string) =>
              researchType === 'Formatted Thesis' || researchType === 'Thesis',
            then: string().notRequired(),
            otherwise: string().required('Please select an attachment type')
          }),
        filename: string().required('Please select a file'),
        researchType: string().required()
      })
    )
  });

  const { handleSubmit, reset } = methods;

  const handleOnClose = useCallback(() => {
    reset();
    setIsOpen(false);
  }, [reset]);

  const renderOpenModalButton = useCallback(
    () => (
      <Button
        buttonType={BUTTON_TYPES.BUTTON}
        className={styles.attachmentFormButton}
        disabled={isSubmitting}
        faIcon={attachment ? null : faPlus}
        onClick={() => setIsOpen(true)}
        size={BUTTON_SIZES.SMALL}
        text={`${attachment ? 'Edit' : `Attach ${researchType}`}`}
        type={BUTTON_TYPES.LINK}
      />
    ),
    [attachment, isSubmitting, researchType]
  );

  const handleOnSubmit = handleSubmit(values => {
    onSubmitCallback(values);
    handleOnClose();
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleOnClose}
      renderOpenModalButton={renderOpenModalButton}
      title={`${attachment ? 'Edit' : 'Attach'} ${researchType}`}
    >
      <FormProvider {...methods}>
        <div className={styles.fields}>
          {isFormattedThesis || isThesis ? null : (
            <SelectField
              label="Attachment Type"
              name="attachmentType"
              options={labelize(ideaAttachmentTypes[researchType || ''])}
            />
          )}
          <AttachmentDropzoneField isFormattedThesis={isFormattedThesis} isThesis={isThesis} />
        </div>
        <div className={styles.submissionButtons}>
          <Button
            buttonType={BUTTON_TYPES.BUTTON}
            disabled={isSubmitting}
            onClick={handleOnSubmit}
            size={BUTTON_SIZES.SMALL}
            text={`${attachment ? 'Update' : 'Attach'} ${researchType}`}
            type={BUTTON_TYPES.PRIMARY}
          />
          <Button
            buttonType={BUTTON_TYPES.BUTTON}
            disabled={isSubmitting}
            onClick={handleOnClose}
            size={BUTTON_SIZES.SMALL}
            text="Cancel"
          />
        </div>
      </FormProvider>
    </Modal>
  );
};

export default AttachmentForm;
